<template>
  <s-modal
    v-model="showModal"
    class="profile-delete-modal"
    ok-confirm-message-code="MC011"
    :title="$t('profile-manager.removing.confirm-modal.title')"
    :ok-title="$t('profile-manager.query.delete-profile')"
    :confirm-type="confirmationType"
    @ok="confirm"
    @cancel="dispose"
    @hide="dispose"
  >
    <div
      v-if="validationRemoveData"
      class="profile-delete-modal__content"
    >
      <div class="profile-delete-modal__message-container">
        <div class="profile-delete-modal__message-warning">
          <i
            class="fa fa-exclamation-triangle profile-delete-modal__warning-icon"
            aria-hidden="true"
          />
          <span class="profile-delete-modal__message--highlight">
            {{$t('profile-manager.removing.confirm-modal.warning')}}:
          </span>
        </div>
        <div
          class="profile-delete-modal__message"
          v-html="$tc('profile-manager.removing.confirm-modal.confirm-message-part1',
          validationRemoveData.profilesRemovedTotal,
          {
            profilesRemoved: validationRemoveData.profilesRemovedTotal
          })
          + '<br/>' +
          $tc('profile-manager.removing.confirm-modal.confirm-message-part2',
          validationRemoveData.usersRemovedTotal,
          {
            usersRemoved: validationRemoveData.usersRemovedTotal
          })"
        >
        </div>
      </div>
      <hr class="profile-delete-modal__separation">
      <p
        class="profile-delete-modal__table-title mb-4"
        v-html="$t('profile-manager.removing.confirm-modal.table-title') + ':'"
      >
      </p>
      <s-table
        class="profile-delete-modal__table no-align"
        :items="validationRemoveData.profilesWillBeRemoved"
        :fields="fields"
      >
        <template #head(usersWithoutAccessTotal)>
          <div class="profile-delete-modal__column-tooltip-container">
            <div class="profile-delete-modal__column-tooltip-title-container">
              {{
                $t(
                  'profile-manager.removing.confirm-modal.table-column-header-users-without-access'
                )
              }}
            </div>
            <div class="profile-delete-modal__tooltip-icon-container">
              <i
                class="fas fa-info-circle profile-delete-modal__tooltip-icon--blue"
                id="profile-delete-modal__selection-tip"
                aria-hidden="true"
              />
            </div>
            <b-tooltip
              target="profile-delete-modal__selection-tip"
              placement="right"
              boundary="viewport"
            >
              <span>
                {{$t('profile-manager.removing.confirm-modal.table-column-header-tooltip')}}.
              </span>
            </b-tooltip>
          </div>
        </template>
      </s-table>
    </div>
  </s-modal>
</template>

<script>
import ConfirmationTypes from '@/constants/confirmationTypes';

export default {
  props: {
    value: {
      type: Boolean,
    },
    validationRemoveData: {
      type: Object,
    },
  },
  data() {
    return {
      confirmationType: ConfirmationTypes.YES_NO,
      deferred: null,
      waitingConfirm: false,
    };
  },
  computed: {
    showModal: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    fields() {
      return [
        {
          key: 'profileName',
          sortable: false,
          label: this.$t('profile-manager.removing.confirm-modal.table-column-header-profile-name'),
          tdClass: 'profile-delete-modal__profile-name-word-break text-wrap',
          thClass: 'profile-delete-modal__table-header-column text-left',
        },
        {
          key: 'associatedUsersTotal',
          sortable: false,
          label: this.$t('profile-manager.removing.confirm-modal.table-column-header-associated-users'),
          thClass: 'text-wrap profile-delete-modal__associated-users-total-column-width',
          class: 'profile-delete-modal__table-header-column profile-delete-modal__associated-users-total-column-width',
        },
        {
          key: 'usersWithoutAccessTotal',
          sortable: false,
          class: 'profile-delete-modal__table-header-column',
          thClass: 'text-wrap profile-delete-modal__column-users-without-access-total-width',
          tdClass: 'profile-delete-modal__table-header-column--danger profile-delete-modal__column-users-without-access-total-width',
        },
      ];
    },
  },
  methods: {
    show() {
      this.showModal = true;
      this.destroyPromise();
      return this.getPromise();
    },
    getPromise() {
      if (!this.deferred) {
        this.deferred = {};
        this.deferred.promise = new Promise((resolve, reject) => {
          this.deferred.resolve = resolve;
          this.deferred.reject = reject;
        });
      }
      return this.deferred.promise;
    },
    destroyPromise() {
      if (this.deferred) {
        this.deferred.reject();
      }
      this.deferred = null;
    },
    confirm() {
      this.deferred.resolve();
      this.showModal = false;
    },
    dispose() {
      if (this.deferred) {
        this.deferred.reject();
      }
      this.showModal = false;
    },
  },
};
</script>

<style lang="sass" scoped>
  @import "src/styles/defaults/variables"
  @import "src/styles/defaults/mobile-first"

  .profile-delete-modal
    &__content
      display: flex
      flex-direction: column
    &__table-title
      margin-top: 5px
      text-align: center
      ::v-deep &--highlight
        font-weight: bold
        text-transform: uppercase
    &__separation
      width: 100%
      background-color: var(--color-treeview-background-hover)
    &__message-container
      display: flex
      flex-direction: column
      margin: 0
      padding-bottom: 5px
    &__message-warning
      margin-top: 5px
      width: 275px
    &__message
      margin-top: 10px
    &__table ::v-deep
      tbody
        display: block
        height: 230px
        overflow: auto
      tbody, thead
        tr
          display: table
          width: 100%
          table-layout: fixed
          td
            word-break: break-word
    ::v-deep &__table-header-column
      text-align: center
      vertical-align: middle
    ::v-deep &__table-header-column--danger
      vertical-align: middle
      text-align: center
      color: var(--color-danger)
    ::v-deep &__column-users-without-access-total-width
      width: 40%
    ::v-deep &__associated-users-total-column-width
      width: 30%
    &__warning-icon
      font-size: 30px
      margin-right: 5px
    ::v-deep &__message--highlight
      font-weight: bold
      text-transform: uppercase
    &__column-tooltip-title-container
      order: 2
    &__tooltip-icon-container
      order: 1
    &__tooltip-icon--blue
      color: var(--color-info)
    &__column-tooltip-container
      display: flex
      flex-direction: column
    ::v-deep .modal-footer
      +media-xs()
        flex-wrap: wrap
        > *
          margin-bottom: .5em
          width: 100%
          margin-left: 0
          margin-right: 0
    +media-min-sm()
      &__message-container
        flex-direction: row
      &__column-tooltip-container
        display: flex
        flex-direction: row
      &__column-tooltip-title-container
        display: inline
        order: 1
      &__tooltip-icon-container
        display: inline
        order: 2
    +media-min-md()
    +media-min-lg()
</style>
